/**
 * The Opportunity Feed — §16. The main screen, and the one the whole product
 * is judged on.
 *
 * ── It is a feed, not a table ───────────────────────────────────────────────
 *
 * §16 asks for something that "feels like a live intelligence feed". So there
 * are no columns, no header row and no density control: a photograph, a score,
 * two numbers and four decisions, repeated. The chrome above the list is a
 * scroll-snapping rail of tiles and one row of sort chips, and everything else
 * that could have gone there did not.
 *
 * ── The contrast strip, and why it is not optional ──────────────────────────
 *
 * Acceptance §17 #3: *sorting by expected landed cost must put a different
 * opportunity on top than sorting by asking price.* That is the proof of
 * non-negotiable principle 6 and it is the entire thesis of this application —
 * and it is worth nothing if the owner has to sort twice and remember what was
 * at the top the first time.
 *
 * So the feed asks the server for BOTH leaders — two `limit=1` requests under
 * the current filter, one sorted by `asking` and one by `landed` — and when
 * they are different rows it says so, above the list, with both numbers on
 * both cards. Two rows of JSON to make the product's central claim visible
 * instead of merely true. When they are the SAME row the strip does not
 * appear: there is no contrast to point at, and inventing one would be worse
 * than saying nothing.
 *
 * ── Sorting moves the emphasis, not just the order ──────────────────────────
 *
 * `OpportunityCard` draws whichever figure the current sort ranks by at 25px
 * and the other at 16px. Switching from ASKING to LANDED therefore reorders
 * the list AND moves the weight across every card at once, which is the same
 * claim said a second way for anybody who scrolled past the strip.
 *
 * ── Filters come from the URL, and go back to the server unread ─────────────
 *
 * A dashboard tile links to `/feed?<the very filter it counted>`. This screen
 * copies that query string onto `GET /api/feed` verbatim — it never parses,
 * normalises or rebuilds it — because acceptance #49 asserts the tile's count
 * equals what the feed returns for the same filter, and a second filter parser
 * on this side is a second filter parser to drift. The chips below the sort
 * row *describe* the query for a person and can remove a key from it; they
 * never reinterpret one.
 *
 * ── Paging ──────────────────────────────────────────────────────────────────
 *
 * `feed.ts`'s cursor carries the ids already emitted so a card rescored mid-page
 * is neither dropped nor repeated — but it says plainly that past `SEEN_CAP`
 * the no-repeat guarantee degrades to plain keyset. So the append here dedupes
 * on id as well. A duplicate card is a visible bug in a feed and the fix costs
 * one `Set`.
 *
 * ── §16, §AF, §10.3 ─────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from 'react'
import { get } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { navigate, useRoute } from '../lib/route'
import { DASH, money, range } from '../lib/format'
import { laneLabel, riskLevelLabel, stageLabel, structureLabel } from '../lib/domain'
import Icon from '../components/Icon'
import OpportunityCard, { type Emphasis, type FeedCard, type FeedPage } from '../components/OpportunityCard'
import Dashboard, { useMinWidth } from './Dashboard'
import { useFeatures } from '../lib/features'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  sortBy: 'Sort by',
  sort_score: 'Score',
  sort_landed: 'Landed cost',
  sort_asking: 'Asking price',
  sort_recent: 'Latest activity',
  sort_discovered: 'Newest',

  contrastTitle: 'These are not the same lead',
  contrastWhy: 'Landed cost is the number that decides. The cheapest sticker is not the cheapest homestead.',
  cheapestAsk: 'CHEAPEST TO BUY',
  cheapestLanded: 'CHEAPEST TO LAND',
  ask: 'ask',
  landed: 'landed',
  noLanded: 'not costed',

  loading: 'Loading…',
  more: 'Load more',
  end: 'That is everything that matches.',
  empty: 'Nothing matches.',
  emptyAll: 'Nothing here yet. Add the first lead with the + button.',
  emptyClear: 'Show everything',
  failed: 'The feed could not load.',
  offline: 'No connection.',
  retry: 'Try again',
  refreshing: 'Refreshing…',
  pull: 'Pull to refresh',
  release: 'Release to refresh',

  filtered: 'Filtered',
  clearAll: 'Clear',
  f_needsReview: 'Needs review',
  f_finalist: 'Finalists',
  f_favorite: 'Favourites',
  f_watched: 'Watched',
  f_dormant: 'Gone quiet',
  f_mayBeGone: 'May be gone',
  f_discovered_today: 'Found today',
  f_actionDue_today: 'Due today',
  f_minScore: 'Score {n}+',
  f_maxLandedCents: 'Lands under {n}',
  f_disposition: 'Status: {n}',
  f_risk: 'Risk: {n}',
  f_zone: 'Zone: {n}',
  f_stage: 'Stage: {n}',
  f_lane: 'Lane: {n}',
  f_structureType: '{n}',
  remove: 'Remove this filter',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  sortBy: 'Ordenar por',
  sort_score: 'Puntuación',
  sort_landed: 'Costo puesto aquí',
  sort_asking: 'Precio pedido',
  sort_recent: 'Actividad reciente',
  sort_discovered: 'Más nuevos',

  contrastTitle: 'No son el mismo prospecto',
  contrastWhy: 'El costo puesto aquí es el número que decide. El más barato en el anuncio no es el más barato en la finca.',
  cheapestAsk: 'MÁS BARATO DE COMPRAR',
  cheapestLanded: 'MÁS BARATO DE TRAER',
  ask: 'piden',
  landed: 'puesto aquí',
  noLanded: 'sin calcular',

  loading: 'Cargando…',
  more: 'Ver más',
  end: 'Eso es todo lo que coincide.',
  empty: 'Nada coincide.',
  emptyAll: 'Todavía no hay nada. Añade el primero con el botón +.',
  emptyClear: 'Ver todo',
  failed: 'No se pudo cargar.',
  offline: 'Sin conexión.',
  retry: 'Reintentar',
  refreshing: 'Actualizando…',
  pull: 'Hala para actualizar',
  release: 'Suelta para actualizar',

  filtered: 'Filtrado',
  clearAll: 'Quitar',
  f_needsReview: 'Falta revisar',
  f_finalist: 'Finalistas',
  f_favorite: 'Favoritos',
  f_watched: 'Vigilados',
  f_dormant: 'Sin movimiento',
  f_mayBeGone: 'Quizás ya no está',
  f_discovered_today: 'Encontrados hoy',
  f_actionDue_today: 'Para hoy',
  f_minScore: 'Puntuación {n}+',
  f_maxLandedCents: 'Puesto aquí bajo {n}',
  f_disposition: 'Estado: {n}',
  f_risk: 'Riesgo: {n}',
  f_zone: 'Zona: {n}',
  f_stage: 'Etapa: {n}',
  f_lane: 'Carril: {n}',
  f_structureType: '{n}',
  remove: 'Quitar este filtro',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/* ────────────────────────────────────────────────────────────────────────────
 * Sorts — the same five `feed.ts` accepts, and no sixth
 * ────────────────────────────────────────────────────────────────────────── */

const SORTS = ['score', 'landed', 'asking', 'recent', 'discovered'] as const
type Sort = (typeof SORTS)[number]

const isSort = (value: string | null): value is Sort =>
  value !== null && (SORTS as readonly string[]).includes(value)

const EMPHASIS_OF: Record<Sort, Emphasis> = {
  score: 'score',
  landed: 'landed',
  asking: 'asking',
  recent: 'recent',
  discovered: 'recent',
}

const PAGE_SIZE = 20

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function Feed() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey): string => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()
  const on = useFeatures()
  const wide = useMinWidth(900)

  const sort: Sort = isSort(route.query.get('sort')) ? (route.query.get('sort') as Sort) : 'score'

  /*
   * The filter, as the exact string the tile handed us minus the two keys this
   * screen owns. Serialised so an effect can depend on it: a `URLSearchParams`
   * is a new object every render and would restart the fetch forever.
   */
  const filterQuery = useMemo(() => {
    const params = new URLSearchParams(route.query)
    params.delete('sort')
    params.delete('cursor')
    params.sort()
    return params.toString()
  }, [route.query])

  const [cards, setCards] = useState<FeedCard[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [paging, setPaging] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [snack, setSnack] = useState<string | null>(null)

  const say = useCallback((message: string) => {
    setSnack(message)
    window.setTimeout(() => setSnack((held) => (held === message ? null : held)), 4000)
  }, [])

  const pathFor = useCallback(
    (nextQuery: URLSearchParams): string => {
      const text = nextQuery.toString()
      return text ? `/feed?${text}` : '/feed'
    },
    [],
  )

  /* ── Page one, and every refresh ─────────────────────────────────────────── */

  useEffect(() => {
    let live = true
    setState('loading')
    setCards([])
    setCursor(null)

    void (async () => {
      const params = new URLSearchParams(filterQuery)
      params.set('sort', sort)
      params.set('limit', String(PAGE_SIZE))
      try {
        const body = await get<{ feed: FeedPage }>(`/api/feed?${params.toString()}`)
        if (!live) return
        setCards(body.feed.cards)
        setCursor(body.feed.cursor)
        setState('ready')
      } catch {
        if (live) setState('failed')
      }
    })()

    return () => {
      live = false
    }
  }, [filterQuery, sort, refreshKey])

  /* ── The next page ───────────────────────────────────────────────────────── */

  const loadMore = useCallback(async () => {
    if (!cursor || paging) return
    setPaging(true)
    const params = new URLSearchParams(filterQuery)
    params.set('sort', sort)
    params.set('limit', String(PAGE_SIZE))
    params.set('cursor', cursor)
    try {
      const body = await get<{ feed: FeedPage }>(`/api/feed?${params.toString()}`)
      setCards((held) => {
        /* Past the cursor's `seen` cap the server's no-repeat guarantee
           degrades to plain keyset, and it says so. A duplicate card is a
           visible bug in a feed; a `Set` is the whole fix. */
        const known = new Set(held.map((card) => card.id))
        return [...held, ...body.feed.cards.filter((card) => !known.has(card.id))]
      })
      setCursor(body.feed.cursor)
    } catch {
      say(t('offline'))
    } finally {
      setPaging(false)
    }
  }, [cursor, filterQuery, paging, sort, say, t])

  /* Auto-paging: a sentinel below the last card. The button stays, because an
     observer that never fires on an old browser would leave no way forward. */
  const sentinel = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = sentinel.current
    if (!node || !cursor || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadMore()
      },
      { rootMargin: '600px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [cursor, loadMore])

  /* ── Pull to refresh ─────────────────────────────────────────────────────── */

  const refresh = useCallback(() => {
    setRefreshKey((n) => n + 1)
  }, [])

  const pull = usePullToRefresh(refresh)

  /* ── Card writes ─────────────────────────────────────────────────────────── */

  const onChanged = useCallback((id: string, change: 'passed' | 'favorite' | 'stage') => {
    if (change === 'passed') {
      /* Removed locally rather than by refetching the page: a refetch would
         reorder everything underneath a thumb that is mid-scroll. */
      setCards((held) => held.filter((card) => card.id !== id))
      setRefreshKey((n) => n + 1)
    }
  }, [])

  /* ── The two leaders ─────────────────────────────────────────────────────── */

  /* Asked for unconditionally, shown only when the strip is switched on: two
     `limit=1` requests, and a strip that appears the moment somebody turns it
     on rather than on the next refresh. */
  const contrast = useContrast(filterQuery, refreshKey)

  /* ── Render ──────────────────────────────────────────────────────────────── */

  const activeHref = pathFor(new URLSearchParams(route.query))
  const chips = describeFilter(route.query, locale, t)

  const list = (
    <>
      {contrast && on('feed.contrast') && (
        <ContrastStrip
          cheapestAsk={contrast.asking}
          cheapestLanded={contrast.landed}
          locale={locale}
          t={t}
          onSort={(next) => navigate(pathFor(withSort(route.query, next)), { replace: true })}
        />
      )}

      <SortRow
        sort={sort}
        t={t}
        onSort={(next) => navigate(pathFor(withSort(route.query, next)), { replace: true })}
      />

      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '0 0 12px' }}>
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => navigate(pathFor(without(route.query, chip.key)))}
              title={t('remove')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                minHeight: 32,
                padding: '0 10px',
                borderRadius: 999,
                border: '1px solid var(--brand)',
                background: 'var(--brand-soft)',
                color: 'var(--brand-strong)',
                font: 'inherit',
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {chip.text}
              <Icon name="close" size={12} />
            </button>
          ))}
          <button
            type="button"
            onClick={() => navigate('/feed')}
            style={{
              minHeight: 32,
              padding: '0 10px',
              borderRadius: 999,
              border: '1px solid var(--line)',
              background: 'transparent',
              color: 'var(--muted)',
              font: 'inherit',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('clearAll')}
          </button>
        </div>
      )}

      {state === 'loading' && <Skeletons />}

      {state === 'failed' && (
        <div style={{ textAlign: 'center', padding: '32px 8px' }}>
          <p style={{ fontWeight: 700, margin: 0 }}>{t('failed')}</p>
          <button
            type="button"
            onClick={refresh}
            style={{
              marginTop: 12,
              minHeight: 46,
              padding: '0 18px',
              borderRadius: 10,
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              color: 'var(--ink)',
              font: 'inherit',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {t('retry')}
          </button>
        </div>
      )}

      {state === 'ready' && cards.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 12px', color: 'var(--muted)' }}>
          <Icon name="search" size={28} />
          <p style={{ fontWeight: 700, color: 'var(--ink)', margin: '10px 0 0' }}>
            {chips.length ? t('empty') : t('emptyAll')}
          </p>
          {chips.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/feed')}
              style={{
                marginTop: 12,
                minHeight: 46,
                padding: '0 18px',
                borderRadius: 10,
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                color: 'var(--ink)',
                font: 'inherit',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {t('emptyClear')}
            </button>
          )}
        </div>
      )}

      {cards.map((card, index) => (
        <OpportunityCard
          key={card.id}
          card={card}
          locale={locale}
          emphasis={EMPHASIS_OF[sort]}
          /* The place in the order, but only where the order is a claim about
             money and only for the top three — a rank badge on card nineteen
             is decoration. */
          rank={(sort === 'landed' || sort === 'asking') && index < 3 ? index + 1 : undefined}
          onChanged={onChanged}
          onSay={say}
        />
      ))}

      <div ref={sentinel} />

      {cursor && (
        <button
          type="button"
          onClick={() => void loadMore()}
          disabled={paging}
          style={{
            width: '100%',
            minHeight: 48,
            borderRadius: 10,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink)',
            font: 'inherit',
            fontWeight: 700,
            cursor: 'pointer',
            marginTop: 4,
          }}
        >
          {paging ? t('loading') : t('more')}
        </button>
      )}

      {state === 'ready' && !cursor && cards.length > 0 && (
        <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, margin: '14px 0 0' }}>
          {t('end')}
        </p>
      )}
    </>
  )

  return (
    <div {...pull.handlers}>
      {pull.offset > 0 && (
        <div
          style={{
            height: pull.offset,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
            fontSize: 12.5,
            fontWeight: 700,
            overflow: 'hidden',
          }}
        >
          {pull.refreshing ? t('refreshing') : pull.armed ? t('release') : t('pull')}
        </div>
      )}

      {/* The counting tiles are a switch, and the two-column layout only earns
          its width when there is something to put in the left column. With the
          rail off the feed is a single centred column at every size. */}
      {on('feed.dashboard') && wide ? (
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
          <aside style={{ flex: '0 0 232px', position: 'sticky', top: 12 }}>
            <Dashboard orientation="column" activeHref={activeHref} refreshKey={refreshKey} />
          </aside>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>{list}</div>
        </div>
      ) : (
        /* One centred reading column. A listing card stretched across a 1240px
           desktop is unreadable — the photograph is the point and it should be
           a photograph, not a banner. */
        <div className="feed-column">
          {on('feed.dashboard') && (
            <Dashboard orientation="rail" activeHref={activeHref} refreshKey={refreshKey} />
          )}
          {list}
        </div>
      )}

      {snack && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 'calc(var(--tabbar-h) + 12px)',
            zIndex: 70,
            maxWidth: 520,
            margin: '0 auto',
            padding: '11px 14px',
            borderRadius: 12,
            background: 'var(--ink)',
            color: 'var(--bg)',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: 'var(--elev-2)',
          }}
        >
          {snack}
        </div>
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The contrast strip
 * ────────────────────────────────────────────────────────────────────────── */

interface Contrast {
  asking: FeedCard
  landed: FeedCard
}

/**
 * The cheapest sticker and the cheapest homestead, under the current filter.
 *
 * Two `limit=1` requests. They could have been computed from the page already
 * on screen — and that would be wrong: the page holds the first twenty rows
 * under ONE sort, and the leader of the other sort is very often not in it.
 * An answer derived from the wrong twenty rows would be confidently incorrect,
 * which on this particular claim is the worst possible failure.
 *
 * `null` — the strip does not render — when either request fails, when either
 * comes back empty, when the landed leader has no landed cost to lead on, or
 * when both leaders are the same row. Only a real contrast is shown.
 */
function useContrast(filterQuery: string, refreshKey: number): Contrast | null {
  const [contrast, setContrast] = useState<Contrast | null>(null)

  useEffect(() => {
    let live = true
    setContrast(null)

    void (async () => {
      const one = async (sort: string): Promise<FeedCard | null> => {
        const params = new URLSearchParams(filterQuery)
        params.set('sort', sort)
        params.set('limit', '1')
        try {
          const body = await get<{ feed: FeedPage }>(`/api/feed?${params.toString()}`)
          return body.feed.cards[0] ?? null
        } catch {
          return null
        }
      }

      const [asking, landed] = await Promise.all([one('asking'), one('landed')])
      if (!live) return
      if (!asking || !landed) return
      if (asking.id === landed.id) return
      /* A row with no landed cost sorts last under `landed`, so leading that
         sort with nothing costed means there is nothing to compare. */
      if (landed.landed?.expected == null) return
      setContrast({ asking, landed })
    })()

    return () => {
      live = false
    }
  }, [filterQuery, refreshKey])

  return contrast
}

function ContrastStrip({
  cheapestAsk,
  cheapestLanded,
  locale,
  t,
  onSort,
}: {
  cheapestAsk: FeedCard
  cheapestLanded: FeedCard
  locale: Locale
  t: (key: CopyKey) => string
  onSort: (sort: Sort) => void
}) {
  const half = (card: FeedCard, label: string, sort: Sort, tinted: boolean) => {
    const landedText =
      card.landed && card.landed.low !== null && card.landed.high !== null
        ? range(
            { low: card.landed.low, expected: card.landed.expected ?? card.landed.low, high: card.landed.high },
            locale,
          )
        : card.landed?.expected != null
          ? money(card.landed.expected, locale)
          : t('noLanded')

    return (
      <button
        type="button"
        onClick={() => onSort(sort)}
        style={{
          flex: '1 1 0',
          minWidth: 0,
          textAlign: 'left',
          padding: '9px 10px',
          borderRadius: 10,
          border: `1px solid ${tinted ? 'var(--money)' : 'var(--line)'}`,
          background: tinted ? 'var(--money-bg)' : 'var(--bg)',
          color: 'var(--ink)',
          font: 'inherit',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            display: 'block',
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: '0.07em',
            color: tinted ? 'var(--money)' : 'var(--muted)',
          }}
        >
          {label}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.25,
            margin: '4px 0 5px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {card.title}
        </span>
        <span style={{ display: 'block', fontSize: 12.5 }}>
          <b>{money(card.askingPriceCents, locale)}</b>{' '}
          <span style={{ color: 'var(--muted)' }}>{t('ask')}</span>
        </span>
        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--money)', fontWeight: 700 }}>
          {landedText} <span style={{ fontWeight: 500 }}>{t('landed')}</span>
        </span>
      </button>
    )
  }

  return (
    <section
      style={{
        border: '1px solid var(--line)',
        borderRadius: 14,
        background: 'var(--surface)',
        padding: 10,
        marginBottom: 14,
      }}
    >
      <p
        style={{
          margin: '0 0 8px',
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          fontSize: 12.5,
          fontWeight: 800,
        }}
      >
        <span style={{ color: 'var(--money)' }}>
          <Icon name="chart" size={15} />
        </span>
        {t('contrastTitle')}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        {half(cheapestAsk, t('cheapestAsk'), 'asking', false)}
        {half(cheapestLanded, t('cheapestLanded'), 'landed', true)}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.4, color: 'var(--muted)' }}>
        {t('contrastWhy')}
      </p>
    </section>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The sort row
 * ────────────────────────────────────────────────────────────────────────── */

function SortRow({
  sort,
  t,
  onSort,
}: {
  sort: Sort
  t: (key: CopyKey) => string
  onSort: (sort: Sort) => void
}) {
  return (
    <div
      role="group"
      aria-label={t('sortBy')}
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        margin: '0 -16px 12px',
        padding: '0 16px 4px',
        scrollbarWidth: 'none',
      }}
    >
      {SORTS.map((option) => {
        const on = option === sort
        return (
          <button
            key={option}
            type="button"
            onClick={() => onSort(option)}
            aria-pressed={on}
            style={{
              flex: '0 0 auto',
              minHeight: 36,
              padding: '0 13px',
              borderRadius: 999,
              border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
              background: on ? 'var(--ink)' : 'var(--surface)',
              color: on ? 'var(--bg)' : 'var(--muted)',
              font: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {t(`sort_${option}` as CopyKey)}
          </button>
        )
      })}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * Describing the query
 *
 * These chips DESCRIBE the query string and can delete a key from it. They
 * never interpret one: no chip changes what is sent, and a key with no local
 * copy still gets a chip, humanised, so a filter can never be invisibly on.
 * ────────────────────────────────────────────────────────────────────────── */

interface FilterChip {
  key: string
  text: string
}

function describeFilter(
  query: URLSearchParams,
  locale: Locale,
  t: (key: CopyKey) => string,
): FilterChip[] {
  const chips: FilterChip[] = []
  const flag = (key: string, copy: CopyKey) => {
    const raw = query.get(key)
    if (raw === '1' || raw === 'true') chips.push({ key, text: t(copy) })
  }

  flag('needsReview', 'f_needsReview')
  flag('finalist', 'f_finalist')
  flag('favorite', 'f_favorite')
  flag('watched', 'f_watched')
  flag('dormant', 'f_dormant')
  flag('mayBeGone', 'f_mayBeGone')

  if (query.get('discovered') === 'today') chips.push({ key: 'discovered', text: t('f_discovered_today') })
  if (query.get('actionDue') === 'today') chips.push({ key: 'actionDue', text: t('f_actionDue_today') })

  const minScore = query.get('minScore')
  if (minScore) chips.push({ key: 'minScore', text: t('f_minScore').replace('{n}', minScore) })

  const maxLanded = query.get('maxLandedCents')
  if (maxLanded) {
    const cents = Number.parseInt(maxLanded, 10)
    chips.push({
      key: 'maxLandedCents',
      text: t('f_maxLandedCents').replace('{n}', Number.isFinite(cents) ? money(cents, locale) : DASH),
    })
  }

  const stage = query.get('stage')
  if (stage) {
    const names = stage
      .split(',')
      .map((one) => stageLabel(one.trim(), locale))
      .join(', ')
    chips.push({ key: 'stage', text: t('f_stage').replace('{n}', names) })
  }

  const lane = query.get('lane')
  if (lane) chips.push({ key: 'lane', text: t('f_lane').replace('{n}', laneLabel(lane, locale)) })

  const risk = query.get('risk')
  if (risk) chips.push({ key: 'risk', text: t('f_risk').replace('{n}', riskLevelLabel(risk, locale)) })

  const structure = query.get('structureType')
  if (structure) {
    chips.push({ key: 'structureType', text: t('f_structureType').replace('{n}', structureLabel(structure, locale)) })
  }

  const zone = query.get('zone')
  if (zone) chips.push({ key: 'zone', text: t('f_zone').replace('{n}', zone) })

  /* `open` is the server's default and saying so would put a chip on a screen
     that has no filter at all. Anything else genuinely changed the question. */
  const disposition = query.get('disposition')
  if (disposition && disposition !== 'open') {
    chips.push({ key: 'disposition', text: t('f_disposition').replace('{n}', disposition) })
  }

  const described = new Set(chips.map((chip) => chip.key))
  for (const [key] of query) {
    if (key === 'sort' || key === 'cursor' || key === 'limit') continue
    if (described.has(key)) continue
    if (key === 'disposition') continue
    described.add(key)
    chips.push({ key, text: `${key}: ${query.get(key) ?? ''}` })
  }

  return chips
}

function withSort(query: URLSearchParams, sort: Sort): URLSearchParams {
  const next = new URLSearchParams(query)
  next.delete('cursor')
  if (sort === 'score') next.delete('sort')
  else next.set('sort', sort)
  return next
}

function without(query: URLSearchParams, key: string): URLSearchParams {
  const next = new URLSearchParams(query)
  next.delete(key)
  next.delete('cursor')
  return next
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pull to refresh
 *
 * Touch only, and only from a scroll position of zero, so it can never fight
 * the browser's own overscroll or a horizontal swipe across the tile rail.
 * ────────────────────────────────────────────────────────────────────────── */

const PULL_TRIGGER = 72
const PULL_MAX = 96

function usePullToRefresh(onRefresh: () => void) {
  const [offset, setOffset] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const start = useRef<number | null>(null)
  /*
   * The offset is mirrored in a ref so `finish` can read it without a state
   * updater. React may call an updater twice under StrictMode, and an updater
   * that fires the refresh would then fire it twice — a real double fetch
   * caused entirely by reading state the wrong way.
   */
  const held = useRef(0)

  const move = useCallback((next: number) => {
    held.current = next
    setOffset(next)
  }, [])

  const onTouchStart = useCallback((event: TouchEvent) => {
    if (refreshing) return
    if (typeof window !== 'undefined' && window.scrollY > 0) return
    start.current = event.touches[0]?.clientY ?? null
  }, [refreshing])

  const onTouchMove = useCallback((event: TouchEvent) => {
    if (start.current === null) return
    const y = event.touches[0]?.clientY ?? start.current
    const delta = y - start.current
    /* Damped, so the strip follows the thumb without ever matching it — that
       resistance is what tells a hand something will happen at the end. */
    move(delta <= 0 ? 0 : Math.min(PULL_MAX, delta * 0.45))
  }, [move])

  const finish = useCallback(() => {
    if (start.current === null) return
    start.current = null
    if (held.current >= PULL_TRIGGER * 0.45) {
      move(34)
      setRefreshing(true)
      onRefresh()
      window.setTimeout(() => {
        setRefreshing(false)
        move(0)
      }, 600)
      return
    }
    move(0)
  }, [move, onRefresh])

  return {
    offset,
    refreshing,
    armed: offset >= PULL_TRIGGER * 0.45,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: finish,
      onTouchCancel: finish,
    },
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The loading state
 *
 * Card-shaped, because a spinner on a photo feed makes the first paint look
 * like a different screen and then jump.
 * ────────────────────────────────────────────────────────────────────────── */

function Skeletons() {
  return (
    <>
      {[0, 1, 2].map((n) => (
        <div
          key={n}
          aria-hidden="true"
          style={{
            border: '1px solid var(--line)',
            borderRadius: 14,
            overflow: 'hidden',
            marginBottom: 14,
            background: 'var(--surface)',
            opacity: 1 - n * 0.25,
          }}
        >
          <div style={{ width: '100%', aspectRatio: '16 / 9', background: 'var(--score-weak-bg)' }} />
          <div style={{ padding: 12 }}>
            <div style={{ height: 16, width: '72%', borderRadius: 6, background: 'var(--line)' }} />
            <div style={{ height: 12, width: '40%', borderRadius: 6, background: 'var(--line)', marginTop: 8 }} />
            <div style={{ height: 26, width: '55%', borderRadius: 6, background: 'var(--line)', marginTop: 14 }} />
          </div>
        </div>
      ))}
    </>
  )
}
